import { readEmbeddingBreakerState } from '../../ai/embedding-pipeline.js';
import type { TraceMcpConfig } from '../../config.js';
import type { IndexStats, Store } from '../../db/store.js';
import { getDroppedEventStats } from '../../indexer/watcher.js';
import { getUnindexableSkipStats } from '../../indexer/unindexable-skip-cache.js';
import type { PluginRegistry } from '../../plugin-api/registry.js';
import type { DetectedVersion, ProjectContext } from '../../plugin-api/types.js';
import type { ProgressSnapshot } from '../../progress.js';
import type { WorktreeDeltaSummary } from '../../worktree-delta.js';

interface IndexHealthResult {
  status: 'ok' | 'degraded' | 'empty';
  stats: IndexStats;
  schemaVersion: number;
  /**
   * Session root this health report belongs to. Without it an agent that sees
   * `status: "empty"` cannot tell whether its own root is unindexed or it is
   * simply attached to the wrong root (TRA-1534: session in a Multica workdir
   * narrating "empty session DB" while the target project is indexed elsewhere).
   */
  projectRoot?: string;
  /**
   * Present only when `status` is `"empty"`. Tells the agent exactly which
   * root is empty and where to go next (reindex here vs relay to another
   * registered project) so it doesn't improvise its own explanation.
   */
  next_steps?: string;
  config: {
    dbPath: string;
    includePatterns: string[];
    excludePatterns: string[];
  };
  warnings: string[];
  progress?: ProgressSnapshot;
  /**
   * Present only in a linked git worktree served from the canonical checkout's
   * index: the files this worktree changed relative to what the index holds.
   * Results about those files are flagged `stale_on_branch`.
   */
  worktree?: WorktreeDeltaSummary;
  /**
   * Summary of files skipped as deterministically unindexable (binary/oversize)
   * in the watcher / incremental path.
   */
  unindexableSkips?: {
    total: number;
    binary: number;
    oversize: number;
  };
  /**
   * Vector coverage of the index. Reported whenever symbols exist — NOT only
   * when embeddings are enabled or a previous run failed. Gating it on
   * `ai.enabled` is what let a 52%-covered index and a 0%-covered project
   * both read as healthy with FTS-only search (TRA-1904): without these
   * numbers the caller cannot tell semantic ranking is degraded.
   */
  embedding?: {
    /** Whether this project will produce vectors (ai.enabled + embedding feature). */
    enabled: boolean;
    /** Total indexed symbols — the denominator of the coverage ratio. */
    totalSymbols: number;
    /** Indexed symbols that already have a vector. */
    embedded: number;
    /** Integer percent of symbols with vectors (0–100). */
    coveragePct: number;
    /** Indexed symbols with no vector yet. */
    queued: number;
    /** Epoch ms until which background embedding is paused, if it is. */
    pausedUntil?: number;
    /** Epoch ms of the last failed batch. */
    lastFailureAt?: number;
    /** Message from the last failed batch. */
    lastError?: string;
  };
}

export function getIndexHealth(
  store: Store,
  config: TraceMcpConfig,
  projectRoot?: string,
): IndexHealthResult {
  const stats = store.getStats();
  const warnings: string[] = [];

  let status: 'ok' | 'degraded' | 'empty' = 'ok';
  if (stats.totalFiles === 0) {
    status = 'empty';
  } else if (stats.partialFiles > 0 || stats.errorFiles > 0) {
    status = 'degraded';
    if (stats.partialFiles > 0) warnings.push(`${stats.partialFiles} files parsed partially`);
    if (stats.errorFiles > 0) warnings.push(`${stats.errorFiles} files failed to parse`);
  }

  // Detect linker failures: symbols exist but no edges were linked
  if (stats.totalSymbols > 0 && stats.totalEdges === 0) {
    if (status === 'ok') status = 'degraded';
    warnings.push(
      `${stats.totalSymbols} symbols indexed but 0 edges linked. ` +
        `Call graph queries (get_call_graph, find_usages, get_change_impact) will return empty results. ` +
        `This usually indicates edge resolution failed — check language plugin support and re-run reindex.`,
    );
  } else if (
    stats.totalSymbols > 50 &&
    stats.totalEdges > 0 &&
    stats.totalEdges < stats.totalSymbols * 0.1
  ) {
    // Very low edge-to-symbol ratio suggests partial linker failure
    warnings.push(
      `Low edge density: ${stats.totalEdges} edges for ${stats.totalSymbols} symbols ` +
        `(${Math.round((stats.totalEdges / stats.totalSymbols) * 100)}% ratio). ` +
        `Some call graph queries may return incomplete results.`,
    );
  }

  // The OS dropped fs events at least once this process: everything changed
  // inside those windows was invisible to the watcher until the reconcile pass
  // caught up (TRA-852). Report it — an agent asking about index freshness
  // cannot read the daemon log, which is where this otherwise only exists.
  const { drops, reconciles, suppressed } = getDroppedEventStats();
  if (drops > 0) {
    warnings.push(
      `The OS dropped file-system events ${drops} time(s) since this process started; ` +
        `${reconciles} index reconcile pass(es) were started in response ` +
        `(started, not necessarily finished — a pass that failed is logged, not subtracted). ` +
        `Results served between a drop and its reconcile may have been stale.`,
    );
  }
  // Sustained drop storms (e.g. a watched dir a build/ML run keeps writing
  // into) no longer reconcile per drop — the breaker collapses them into one
  // trailing pass per cooldown (TRA-1665). Surface the coalesced count so a
  // storm is visible here, not just in the daemon log.
  if (suppressed > 0) {
    if (status === 'ok') status = 'degraded';
    warnings.push(
      `Reconcile storm backoff coalesced ${suppressed} dropped-event report(s) since this ` +
        `process started: full-walk reconciles were deferred to one trailing pass per cooldown ` +
        `instead of one walk per drop. Results for churn-heavy roots may lag until the storm subsides.`,
    );
  }

  const versionRow = store.db
    .prepare("SELECT value FROM schema_meta WHERE key = 'schema_version'")
    .get() as { value: string } | undefined;

  const breaker = readEmbeddingBreakerState(store.db);
  let embedding: IndexHealthResult['embedding'];
  // TRA-1904: report vector coverage whenever symbols exist. The old gate
  // (`ai.enabled || breaker`) hid two states that both serve FTS-only
  // results: AI disabled entirely (0 vectors, empty meta — the assetfeed
  // case) and an undrained backlog with no recorded failure (fresh symbols
  // after a reindex, provider briefly unreachable inside one cooldown).
  if (stats.totalSymbols > 0) {
    // Mirrors createAIProvider(): no vectors flow unless the project opted
    // into AI *and* left the embedding capability on. ai.enabled defaults
    // to false, so a project that never configured AI lands here as
    // enabled=false with coverage 0% instead of an absent block.
    const enabled = config.ai?.enabled === true && (config.ai.features?.embedding ?? true);
    const queued = store.countUnembeddedSymbols();
    const embedded = Math.max(0, stats.totalSymbols - queued);
    const coveragePct =
      stats.totalSymbols > 0 ? Math.round((embedded / stats.totalSymbols) * 100) : 100;
    const paused = !!breaker && breaker.disabledUntilMs > Date.now();
    embedding = {
      enabled,
      totalSymbols: stats.totalSymbols,
      embedded,
      coveragePct,
      queued,
      pausedUntil: paused ? breaker.disabledUntilMs : undefined,
      lastFailureAt: breaker?.lastFailureAt || undefined,
      lastError: breaker?.lastError,
    };
    if (!enabled) {
      // Deliberate operator choice (lexical-only is a supported config), so
      // this stays a warning, not degraded — but it must be said out loud:
      // every semantic/hybrid query on this project is FTS-only.
      let msg =
        `Semantic search is disabled (ai.enabled=false or ai.features.embedding=false): ` +
        `${embedded}/${stats.totalSymbols} symbols have vectors but none are used — search is FTS-only. ` +
        `Enable an AI provider and run embed_repo for hybrid ranking.`;
      // A stale breaker row means AI was enabled before and the backfill was
      // failing then — forensics for why vectors are missing, not a live
      // pause (nothing runs while disabled, so no degraded status).
      if (breaker && (breaker.lastError || breaker.disabledUntilMs > Date.now()) && queued > 0) {
        msg +=
          ` A previous embedding run failed before AI was disabled ` +
          `(${breaker.lastError ?? 'unknown error'}); the ${queued}-symbol backlog will ` +
          `not drain until AI is re-enabled.`;
      }
      warnings.push(msg);
    } else if (paused && queued > 0) {
      if (status === 'ok') status = 'degraded';
      warnings.push(
        `${queued} symbols are queued for embedding but background embedding is paused until ` +
          `${new Date(breaker.disabledUntilMs).toISOString()} after repeated failures ` +
          `(${breaker.lastError ?? 'unknown error'}). Semantic and hybrid search results are ` +
          `incomplete until the embedding provider is reachable; call embed_repo to retry now. ` +
          `(vector coverage ${coveragePct}% — ${embedded}/${stats.totalSymbols} symbols embedded)`,
      );
    } else if (breaker?.lastError && queued > 0) {
      // Cooldown expired but no batch has succeeded since: the provider is
      // still unreachable and search is still FTS-only. Without this the
      // degradation goes quiet between cooldown windows (TRA-1798).
      if (status === 'ok') status = 'degraded';
      warnings.push(
        `${queued} symbols are queued for embedding but the last background attempt failed ` +
          `(${breaker.lastError}). Search is FTS-only until the embedding provider is reachable; ` +
          `call embed_repo to retry now. ` +
          `(vector coverage ${coveragePct}% — ${embedded}/${stats.totalSymbols} symbols embedded)`,
      );
    } else if (queued > 0) {
      // No breaker row at all: the backlog was never attempted (fresh reindex
      // + provider down, or background pass not yet fired). Previously this
      // read as healthy with silently partial semantic ranking (TRA-1904).
      if (status === 'ok') status = 'degraded';
      warnings.push(
        `${queued} of ${stats.totalSymbols} symbols have no vector yet ` +
          `(coverage ${coveragePct}%). Background embedding has not drained the backlog — ` +
          `semantic and hybrid ranking cover only the embedded subset; call embed_repo to backfill.`,
      );
    }
  }

  const resolvedRoot = projectRoot ?? (typeof config.root === 'string' ? config.root : undefined);
  const unindexable = resolvedRoot ? getUnindexableSkipStats(resolvedRoot) : undefined;

  return {
    status,
    stats,
    embedding,
    ...(projectRoot ? { projectRoot } : {}),
    ...(unindexable && unindexable.totalEntries > 0
      ? {
          unindexableSkips: {
            total: unindexable.totalEntries,
            binary: unindexable.byReason.binary,
            oversize: unindexable.byReason.oversize,
          },
        }
      : {}),
    // Empty-index disambiguation (TRA-1534). The agent's session root and the
    // project holding the code it wants are often different roots (e.g. an
    // ephemeral Multica workdir vs an indexed project like top100) — name the
    // empty root explicitly and point at the relay instead of letting the
    // agent narrate a vague "empty session DB".
    ...(status === 'empty'
      ? {
          next_steps: projectRoot
            ? `Session root ${projectRoot} holds 0 indexed files. Run reindex to index it, or list_projects + call_project_tool when the code lives in another registered project.`
            : 'Index holds 0 files. Run reindex to index this root, or list_projects + call_project_tool when the code lives in another registered project.',
        }
      : {}),
    schemaVersion: versionRow ? Number(versionRow.value) : 0,
    config: {
      // The path this store was actually opened at, not a config default —
      // an agent that stats or deletes this path must hit the real DB (TRA-802).
      dbPath: store.db.name,
      includePatterns: config.include,
      excludePatterns: config.exclude,
    },
    warnings,
  };
}

interface ProjectMapResult {
  frameworks: string[];
  stats: IndexStats;
  languages: { language: string; count: number }[];
  detectedVersions?: DetectedVersion[];
  dependencySummary?: { total: number; dev: number; byEcosystem: Record<string, number> };
  diagnostics?: string[];
}

interface ProjectMapSummary {
  frameworks: string[];
  fileCount: number;
  symbolCount: number;
  languages: string[];
  detectedVersions?: DetectedVersion[];
  diagnostics?: string[];
}

function getArtifactDiagnostics(store: Store, frameworks: string[]): string[] | undefined {
  if (frameworks.length > 0) return undefined;

  const counts: string[] = [];
  try {
    const r =
      (store.db.prepare('SELECT COUNT(*) as c FROM routes').get() as { c: number } | undefined)
        ?.c ?? 0;
    if (r > 0) counts.push(`${r} routes`);
  } catch {}
  try {
    const c =
      (store.db.prepare('SELECT COUNT(*) as c FROM components').get() as { c: number } | undefined)
        ?.c ?? 0;
    if (c > 0) counts.push(`${c} components`);
  } catch {}
  try {
    const m =
      (store.db.prepare('SELECT COUNT(*) as c FROM migrations').get() as { c: number } | undefined)
        ?.c ?? 0;
    if (m > 0) counts.push(`${m} migrations`);
  } catch {}
  try {
    const o =
      (store.db.prepare('SELECT COUNT(*) as c FROM orm_models').get() as { c: number } | undefined)
        ?.c ?? 0;
    if (o > 0) counts.push(`${o} orm_models`);
  } catch {}

  if (counts.length === 0) return undefined;

  return [
    `Indexed artifacts found (${counts.join(', ')}), but no frameworks were detected. Manifest files (package.json, composer.json, etc.) may be missing or located in unindexed directories.`,
  ];
}

export function getProjectMap(
  store: Store,
  registry: PluginRegistry,
  summaryOnly?: boolean,
  projectContext?: ProjectContext,
): ProjectMapResult | ProjectMapSummary {
  const stats = store.getStats();
  let frameworks: string[];
  if (projectContext) {
    const active = registry.getActiveFrameworkPlugins(projectContext);
    frameworks = active.isOk() ? active.value.map((p) => p.manifest.name) : [];
  } else {
    frameworks = registry.getAllFrameworkPlugins().map((p) => p.manifest.name);
  }

  const detectedVersions = projectContext?.detectedVersions;
  const diagnostics = getArtifactDiagnostics(store, frameworks);

  if (summaryOnly) {
    const languageRows = store.db
      .prepare(
        'SELECT language FROM files WHERE language IS NOT NULL GROUP BY language ORDER BY COUNT(*) DESC',
      )
      .all() as { language: string }[];
    return {
      frameworks,
      fileCount: stats.totalFiles,
      symbolCount: stats.totalSymbols,
      languages: languageRows.map((r) => r.language),
      detectedVersions: detectedVersions?.length ? detectedVersions : undefined,
      diagnostics,
    };
  }

  const languageRows = store.db
    .prepare(
      'SELECT language, COUNT(*) as count FROM files WHERE language IS NOT NULL GROUP BY language ORDER BY count DESC',
    )
    .all() as { language: string; count: number }[];

  let dependencySummary: ProjectMapResult['dependencySummary'];
  if (projectContext?.allDependencies.length) {
    const deps = projectContext.allDependencies;
    const byEcosystem: Record<string, number> = {};
    for (const d of deps) {
      // Infer ecosystem from naming patterns
      let eco = 'other';
      if (d.name.includes('/') && !d.name.includes(':')) eco = 'npm';
      else if (d.name.includes(':')) eco = 'maven';
      else if (d.name.startsWith('@')) eco = 'npm';
      else if (projectContext.packageJson && !d.name.includes('.')) eco = 'npm';
      else if (projectContext.composerJson && d.name.includes('/')) eco = 'composer';
      else if (projectContext.goMod && d.name.includes('/')) eco = 'go';
      else if (projectContext.cargoToml) eco = 'cargo';
      else if (projectContext.gemfile) eco = 'rubygems';
      else if (projectContext.pyprojectToml || projectContext.requirementsTxt) eco = 'pypi';
      else if (projectContext.pomXml || projectContext.buildGradle) eco = 'maven';
      byEcosystem[eco] = (byEcosystem[eco] ?? 0) + 1;
    }
    dependencySummary = {
      total: deps.length,
      dev: deps.filter((d) => d.dev).length,
      byEcosystem,
    };
  }

  return {
    frameworks,
    stats,
    languages: languageRows,
    detectedVersions: detectedVersions?.length ? detectedVersions : undefined,
    dependencySummary,
    diagnostics,
  };
}
